import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import type { ButtonV2Props } from '@withjoy/joykit';
import { PeopleContext } from '@shared/context/people';
import { useContactPickerController } from './useContactPickerController';
import type { HardcodedLabel, HardcodedLabelsMode } from '../PeoplePicker/usePeoplePickerButtonController';
import type { OutsideScopeCopy } from '../PeoplePicker/PeoplePicker.types';
import { PeopleListItem } from '@shared/components/PeopleListItem';
import { useTranslation } from '@shared/core';
import type { DeliveryMethodValue, PeoplePickerUseCase, RenderPickerTrigger } from '../PeoplePicker/PeoplePicker.types';
import type { PersonItemRender } from '../PeoplePicker/PeoplePicker';

export type ContactPickerItemType = Readonly<'simple' | 'withDeliveryMethod'>;

export type ContactPickerButtonProps = Readonly<
  Omit<ButtonV2Props, 'onClick' | 'children' | 'title' | 'itemType'> & {
    buttonTextDefault?: string;
    buttonTextHover?: string;
    eventId: string;
    title?: React.ReactNode;
    hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
    hardcodedLabelsMode?: HardcodedLabelsMode;
    outsideScopeCopy?: OutsideScopeCopy;
    /** Copy for the guest list's select-all control in `requiredScope` mode. See PeoplePicker. */
    selectAllLabel?: string;
    onStartSelection?: () => void;
    useCase: PeoplePickerUseCase;
    // When provided, renders custom trigger UI (e.g. recipient chips) instead of the default
    // ButtonV2; the supplied onClick opens the picker.
    renderTrigger?: RenderPickerTrigger;
    /** Rendered above the guest list inside the dialog; see PeoplePickerDialog. */
    headerBanner?: React.ReactNode;
  } & (
      | {
          itemType?: 'simple';
          enableSmsSelection?: never;
          smsOnly?: never;
          emailOnly?: never;
          initialDeliveryMethod?: never;
          allowUnreachableSelection?: never;
          preferenceStorageKey?: never;
          showDeliveryMethodSelector?: never;
          showPhoneChannel?: never;
        }
      | {
          itemType: 'withDeliveryMethod';
          enableSmsSelection: boolean;
          smsOnly?: boolean;
          emailOnly?: boolean;
          initialDeliveryMethod?: DeliveryMethodValue;
          allowUnreachableSelection?: boolean;
          preferenceStorageKey?: string;
          showDeliveryMethodSelector?: boolean;
          /** Show the WhatsApp or SMS channel per guest (MSG-584). Pass the `useIsSmsEnabled` result. */
          showPhoneChannel?: boolean;
        }
    )
>;

export const ContactPickerButton: React.FC<ContactPickerButtonProps> = ({
  eventId,
  title: _title,
  hardcodedLabels,
  hardcodedLabelsMode,
  outsideScopeCopy,
  selectAllLabel,
  enableSmsSelection = false,
  smsOnly = false,
  emailOnly = false,
  initialDeliveryMethod,
  allowUnreachableSelection: _allowUnreachableSelection,
  preferenceStorageKey,
  showDeliveryMethodSelector,
  showPhoneChannel = false,
  headerBanner,
  itemType = 'simple',
  onStartSelection,
  useCase,
  renderTrigger,

  buttonTextDefault: _buttonTextDefault,
  buttonTextHover,

  intent = 'primary',
  variant = 'outline',
  disabled,
  ...buttonProps
}) => {
  // Simple-mode pickers lack unreachable warning UI, so default to false.
  // withDeliveryMethod pickers have full warning infrastructure, so default to true.
  const allowUnreachableSelection = _allowUnreachableSelection ?? itemType === 'withDeliveryMethod';

  const { tq } = useTranslation('peoplePickerButton');

  const { selectedPeople, setSelectedPeople } = useContext(PeopleContext);

  const title = _title || tq`Select Guests`;
  const buttonTextDefault = _buttonTextDefault || tq`Select Guests`;

  const [isHovering, setIsHovering] = useState(false);

  const personItemRender: PersonItemRender = useCallback(
    person => {
      if (itemType === 'withDeliveryMethod') {
        return (
          <PeopleListItem.WithDeliveryMethod
            enableSmsSelection={enableSmsSelection}
            {...person}
            // MSG-396: Text Blast already says "Phone number may be invalid" in the sublabel, so the
            // row's warning triangle would repeat it.
            phoneInvalid={!smsOnly && person.phoneInvalid}
          />
        );
      }

      return <PeopleListItem.Simple {...person} />;
    },
    [itemType, enableSmsSelection, smsOnly],
  );

  const { loading, startSelection, error } = useContactPickerController({
    eventId,
    title,
    onSelectedPeople: setSelectedPeople,
    enableSmsSelection,
    smsOnly,
    emailOnly,
    initialDeliveryMethod,
    allowUnreachableSelection,
    preferenceStorageKey,
    showDeliveryMethodSelector,
    showPhoneChannel,
    headerBanner,
    hardcodedLabels,
    hardcodedLabelsMode,
    outsideScopeCopy,
    selectAllLabel,
    personItemRender,
    useCase,
  });

  const handleClick = () => {
    // The built-in trigger below gets these as ButtonV2 props, but `renderTrigger` callers are
    // handed them and may not wire them up. Opening while `loading` would show an empty guest
    // list, so the guard belongs on the handler both paths share.
    if (disabled || error || loading) return;
    onStartSelection?.();
    startSelection();
  };

  const buttonText = useMemo(
    () => (isHovering && selectedPeople?.length && buttonTextHover ? buttonTextHover : buttonTextDefault),
    [isHovering, buttonTextHover, buttonTextDefault, selectedPeople],
  );

  // Kept after the hooks above so the early return doesn't change hook order between renders.
  if (renderTrigger) {
    return <>{renderTrigger({ onClick: handleClick, loading, disabled: !!(disabled || error) })}</>;
  }

  return (
    <ButtonV2
      {...buttonProps}
      intent={intent}
      variant={variant}
      disabled={disabled || !!error}
      loading={loading}
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      borderRadius="999px"
    >
      {buttonText}
    </ButtonV2>
  );
};
